import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';
import { catchError, delay, mergeMap, retryWhen } from 'rxjs/operators';
import {
  AUTHENTICATION_ERROR,
  FORBIDDEN_ERROR,
  MAX_NUMBER_OF_RETRIES,
} from '@share/constants';
import { AuthenticationManager } from '../oauth/authentication-manager.service';
import { NGXLogger } from 'ngx-logger';

const getErrorMessage = (maxRetry: number) =>
  `Tried to reach the server ${maxRetry + 1} time(s) without success, giving up. `;

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private auth = inject(AuthenticationService);
  private authenticationManager = inject(AuthenticationManager);
  private logger = inject(NGXLogger);

  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler,
  ): Observable<HttpEvent<T>> {
    // Clone the request to add the new header.
    const authReq = req.clone({
      setHeaders: {
        Authorization: this.auth.getAuthorizationHeader(),
        observe: 'response' as const,
      },
    });
    // Pass on the cloned request instead of the original request.
    return next.handle(authReq).pipe(
      this.delayedRetry(500, MAX_NUMBER_OF_RETRIES, 1000),
      catchError((error) => {
        this.logger.error('Error: ', error);
        return throwError(error);
      }),
    );
  }

  delayedRetry<T>(delayMs: number, maxRetry: number, backoffMs: number) {
    let retries = maxRetry;
    return (src: Observable<T>) =>
      src.pipe(
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (
                error?.status === AUTHENTICATION_ERROR ||
                error?.status === FORBIDDEN_ERROR
              ) {
                this.authenticationManager.logout();
                this.authenticationManager.login();
                return EMPTY;
              } else if (retries-- > 0) {
                const backoffTime = delayMs + (maxRetry - retries) * backoffMs;
                return of(error).pipe(delay(backoffTime));
              }
              const errorMessage =
                'Error:' +
                JSON.stringify(error, (key, val) => {
                  if (typeof val === 'function') {
                    return String(val);
                  }
                  return val;
                });
              return throwError({
                message: getErrorMessage(maxRetry) + errorMessage,
                error: error,
              });
            }),
          ),
        ),
      );
  }
}
